/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { inject, Injectable } from '@angular/core';
import { FabUser } from '@fabric/components/common';

import { AuthenticationService } from '../authentication/authentication.service';

/**
 * Created by michael faith on 3/12/2019.
 */

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public user: FabUser | null = null;

  private readonly authenticationService: AuthenticationService = inject(AuthenticationService);

  constructor() {
    // Construct the UserProfile object
    this.createUser();
  }

  /**
   * Return the user's list of authorities
   * @returns array of authority names
   */
  public getAuthorities(): string[] {
    return this.user?.authorities ? this.user.authorities : [];
  }

  /**
   * Test to see if the current user has any of the provided authorities
   * @param requiredAuthorities - Array of authority names
   * @returns true if the user has any of the authorities, false otherwise
   */
  public hasAnyAuthorities(requiredAuthorities: string[]): boolean {
    for (const authority of requiredAuthorities) {
      if (authority !== null && this.user?.authorities && this.user.authorities.indexOf(authority) > -1) {
        return true;
      }
    }
    return false;
  }

  /**
   * Test if the current user has the provided authority.
   * @param requiredAuthority - Authority name
   * @returns true if the user has the authority, false otherwise
   */
  public hasAuthority(requiredAuthority: string): boolean {
    return this.hasAnyAuthorities([requiredAuthority]);
  }

  /**
   * Check to see if the current user has the provided role.
   * @param requiredRole - Role name
   * @returns true if the user has the role, false otherwise
   */
  public hasRole(requiredRole: string): boolean {
    return this.user?.roles ? this.user.roles.indexOf(requiredRole) > -1 : false;
  }

  /**
   * Create an instance of FabUser using the data provided.
   */
  private createUser(): void {
    const user = this.authenticationService.userInfo;
    if (user) {
      this.user = user;
      this.user.roles ??= [];
      this.user.authorities ??= [];
    }
  }
}
